import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import MasterV2 from "../MasterV2";
import { LoadingDiv, SweetAlertShow } from "../../master/Master";
import { studentLowAttendanceLongAbsentReport, getAttendanceFollowupHistory, addAttendanceFollowup } from "../../../api/Attendance";
const pad = (v) => String(v).padStart(2, "0");
const formatDate = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const today = () => formatDate(new Date());
const daysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return formatDate(d);
};
const readableDate = (v) => {
  if (!v) return "-";
  const d = new Date(`${v}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? v
    : d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};
const safeNumber = (v, f = 0) => (Number.isFinite(Number(v)) ? Number(v) : f);
const initials = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((x) => x[0]?.toUpperCase())
    .join("");
const courseInfo = (s) =>
  s?.latest_course_and_fee && typeof s.latest_course_and_fee === "object"
    ? s.latest_course_and_fee
    : Array.isArray(s?.course_and_fee_info)
      ? s.course_and_fee_info[0] || {}
      : s?.course_and_fee_info || {};
const batchName = (s) =>
  courseInfo(s)?.batch?.title || s?.batch_name || s?.batch?.title || "-";
const courseName = (s) =>
  courseInfo(s)?.course?.title || s?.course_name || s?.course?.title || "-";
const statsOf = (s) => {
  const present = safeNumber(
    s?.present_days ?? s?.present_attendance ?? s?.present,
  );
  const absent = safeNumber(
    s?.absent_days ?? s?.absent_attendance ?? s?.absent,
  );
  const marked = safeNumber(
    s?.marked_days ?? s?.marked_attendance,
    present + absent,
  );
  const percentage = safeNumber(
    s?.attendance_percentage,
    marked ? (present * 100) / marked : 0,
  );
  return { present, absent, marked, percentage };
};
const riskOf = (p) =>
  p < 40
    ? ["critical", "Critical"]
    : p < 60
      ? ["danger", "Very Low"]
      : p < 75
        ? ["warning", "Low"]
        : ["good", "Good"];
export default function StudentLowAttendanceReport() {
  const navigate = useNavigate();
  const requestRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [activeTab, setActiveTab] = useState("absent");
  const [students, setStudents] = useState([]);
  const [callStudent, setCallStudent] = useState(null);
  const [callHistory, setCallHistory] = useState([]);
  const [callLoading, setCallLoading] = useState(false);
  const [callSaving, setCallSaving] = useState(false);
  const [callResponse, setCallResponse] = useState("");
  const [callNextDate, setCallNextDate] = useState("");
  const callRequestRef = useRef(0);
  const quickCallResponses = ["Parent contacted - Will attend", "No answer", "Phone switched off", "Call later", "Student is sick", "Medical leave", "Out of station", "Parent will visit school", "Wrong number", "Other"];

  const openCallModal = async (student) => {
    const requestId = ++callRequestRef.current;
    setCallStudent(student);
    setCallHistory([]);
    setCallResponse("");
    setCallNextDate("");
    setCallLoading(true);
    try {
      const result = await getAttendanceFollowupHistory(student.id);
      if (requestId !== callRequestRef.current) return;
      const body = result?.data || {};
      if (body.status === false) throw new Error(body.message || "Unable to load call history");
      const payload = body.data;
      const history = Array.isArray(payload) ? payload : (payload?.followups || payload?.history || payload?.data || []);
      setCallHistory(Array.isArray(history) ? history : []);
    } catch (error) {
      if (requestId === callRequestRef.current) SweetAlertShow("error", "Call History", error?.response?.data?.message || error?.message || "Unable to load history");
    } finally {
      if (requestId === callRequestRef.current) setCallLoading(false);
    }
  };

  const closeCallModal = () => {
    ++callRequestRef.current;
    setCallStudent(null);
    setCallHistory([]);
  };

  const saveCallResponse = async (event) => {
    event.preventDefault();
    if (!callStudent?.id || !callResponse.trim() || callSaving) return;
    setCallSaving(true);
    try {
      const result = await addAttendanceFollowup({
        user_id: callStudent.id,
        type: "attendance",
        response: callResponse.trim(),
        next_followup_date: callNextDate || null,
      });
      if (result?.data?.status === false) throw new Error(result.data.message || "Unable to save response");
      SweetAlertShow("success", "Saved", "Call response recorded successfully.");
      setCallResponse("");
      setCallNextDate("");
      await openCallModal(callStudent);
    } catch (error) {
      SweetAlertShow("error", "Save Failed", error?.response?.data?.message || error?.message || "Unable to save response");
    } finally {
      setCallSaving(false);
    }
  };

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    session_id: "",
    from_date: daysAgo(29),
    to_date: today(),
    absent_days: "7",
    low_percentage: "75",
  });
  const [pagination, setPagination] = useState({
    total: 0,
    current_page: 1,
    last_page: 1,
    per_page: 50,
  });
  useEffect(() => {
    try {
      const list = JSON.parse(localStorage.getItem("sessions") || "[]");
      setSessions(Array.isArray(list) ? list : []);
    } catch {
      setSessions([]);
    }
  }, []);
  useEffect(() => {
    if (filters.session_id || !sessions.length) return;
    const active = sessions.find((s) => Number(s?.status) === 1);
    if (active?.id)
      setFilters((p) => ({ ...p, session_id: String(active.id) }));
  }, [sessions, filters.session_id]);
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 450);
    return () => clearTimeout(t);
  }, [searchInput]);
  const selectedSession = useMemo(
    () => sessions.find((s) => String(s?.id) === String(filters.session_id)),
    [sessions, filters.session_id],
  );
  const sessionLabel =
    selectedSession?.session || selectedSession?.title || "-";
  const buildQuery = useCallback(
    ({ page = 1, exportMode = false } = {}) => {
      const p = new URLSearchParams();
      p.set("session_id", filters.session_id);
      p.set(
        "report_type",
        activeTab === "absent" ? "long_absent" : "low_attendance",
      );
      if (filters.from_date) p.set("from_date", filters.from_date);
      if (filters.to_date) p.set("to_date", filters.to_date);
      if (activeTab === "absent") p.set("absent_days", filters.absent_days);
      else {
        p.set("percentage", filters.low_percentage);
        p.set("min_marked_days", "2");
      }
      if (search) p.set("search", search);
      if (exportMode) p.set("export", "1");
      else {
        p.set("page", String(page));
        p.set("per_page", String(pagination.per_page));
      }
      return p.toString();
    },
    [filters, activeTab, search, pagination.per_page],
  );
  const normalize = (response) => {
    const root = response?.data || {};
    const d = root?.data || {};
    const list = Array.isArray(d?.students)
      ? d.students
      : Array.isArray(d?.students?.data)
        ? d.students.data
        : [];
    return {
      ok: root?.status !== false,
      list,
      total: safeNumber(d?.total ?? d?.students?.total, list.length),
      current: safeNumber(d?.current_page ?? d?.students?.current_page, 1),
      last: safeNumber(d?.last_page ?? d?.students?.last_page, 1),
      perPage: safeNumber(
        d?.per_page ?? d?.students?.per_page,
        pagination.per_page,
      ),
    };
  };
  const loadReport = useCallback(
    async (page = 1) => {
      if (!filters.session_id) return;
      if (
        filters.from_date &&
        filters.to_date &&
        filters.from_date > filters.to_date
      )
        return SweetAlertShow(
          "warning",
          "Invalid Date Range",
          "From date cannot be greater than To date.",
        );
      const requestId = ++requestRef.current;
      setLoading(true);
      try {
        const response = await studentLowAttendanceLongAbsentReport(
          buildQuery({ page }),
        );
        if (requestId !== requestRef.current) return;
        const r = normalize(response);
        if (!r.ok)
          throw new Error(
            response?.data?.message || "Unable to load attendance report.",
          );
        setStudents(r.list);
        setPagination((p) => ({
          ...p,
          total: r.total,
          current_page: r.current,
          last_page: r.last,
          per_page: r.perPage,
        }));
      } catch (e) {
        if (requestId === requestRef.current) {
          setStudents([]);
          setPagination((p) => ({
            ...p,
            total: 0,
            current_page: 1,
            last_page: 1,
          }));
          SweetAlertShow(
            "error",
            "Attendance Report",
            e?.response?.data?.message ||
              e?.message ||
              "Unable to load report.",
          );
        }
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    },
    [filters.session_id, filters.from_date, filters.to_date, buildQuery],
  );
  useEffect(() => {
    loadReport(1);
  }, [
    loadReport,
    activeTab,
    filters.absent_days,
    filters.low_percentage,
    search,
  ]);
  useEffect(() => {
    if (filters.session_id) loadReport(1); /* eslint-disable-next-line */
  }, [pagination.per_page]);
  const rows = useMemo(
    () => students.map((s) => ({ ...s, __stats: statsOf(s) })),
    [students],
  );
  const pageSummary = useMemo(
    () =>
      rows.reduce(
        (a, s) => ({
          present: a.present + s.__stats.present,
          absent: a.absent + s.__stats.absent,
          marked: a.marked + s.__stats.marked,
        }),
        { present: 0, absent: 0, marked: 0 },
      ),
    [rows],
  );
  const openStudentReport = (student) => {
    const p = new URLSearchParams({
      session_id: filters.session_id,
      from_date: filters.from_date,
      to_date: filters.to_date,
    });
    navigate(
      `/student-attendance/student/${student.id}/report?${p.toString()}`,
    );
  };
  const getExportRows = async () => {
    setLoading(true);
    try {
      const response = await studentLowAttendanceLongAbsentReport(
        buildQuery({ exportMode: true }),
      );
      const r = normalize(response);
      if (!r.ok)
        throw new Error(response?.data?.message || "Unable to export report.");
      return r.list.map((s) => ({ ...s, __stats: statsOf(s) }));
    } catch (e) {
      SweetAlertShow(
        "error",
        "Export",
        e?.response?.data?.message || e?.message || "Unable to export.",
      );
      return [];
    } finally {
      setLoading(false);
    }
  };
  const exportExcel = async () => {
    const all = await getExportRows();
    if (!all.length) return;
    const head = `<tr><th>#</th><th>Student</th><th>Father</th><th>Reg No</th><th>Mobile</th><th>Course</th><th>Batch</th><th>Present</th><th>Absent</th><th>Marked</th><th>Attendance %</th></tr>`;
    const body = all
      .map(
        (s, i) =>
          `<tr><td>${i + 1}</td><td>${s.name || ""}</td><td>${s.father_name || ""}</td><td>${s.registration_no || ""}</td><td>${s.phone || ""}</td><td>${courseName(s)}</td><td>${batchName(s)}</td><td>${s.__stats.present}</td><td>${s.__stats.absent}</td><td>${s.__stats.marked}</td><td>${s.__stats.percentage.toFixed(1)}%</td></tr>`,
      )
      .join("");
    const blob = new Blob([`<table border="1">${head}${body}</table>`], {
      type: "application/vnd.ms-excel;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${activeTab === "absent" ? "long-absent" : "low-attendance"}-${filters.to_date}.xls`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  const printReport = async () => {
    const all = await getExportRows();
    if (!all.length) return;
    const w = window.open("", "_blank", "width=1400,height=900");
    if (!w)
      return SweetAlertShow(
        "info",
        "Popup Blocked",
        "Please allow popups to print.",
      );
    const body = all
      .map(
        (s, i) =>
          `<tr><td>${i + 1}</td><td><b>${s.name || "-"}</b><br><small>${s.father_name || ""}</small></td><td>${s.registration_no || "-"}</td><td>${s.phone || "-"}</td><td>${batchName(s)}<br><small>${courseName(s)}</small></td><td>${s.__stats.present}</td><td>${s.__stats.absent}</td><td>${s.__stats.marked}</td><td><b>${s.__stats.percentage.toFixed(1)}%</b></td></tr>`,
      )
      .join("");
    w.document.write(
      `<!doctype html><html><head><title>Attendance Attention Report</title><style>body{font-family:Arial;padding:12px;color:#222}h2{margin:0;font-size:18px}p{font-size:10px;color:#666}table{width:100%;border-collapse:collapse;margin-top:12px}th,td{border:1px solid #bbb;padding:6px;font-size:9px;text-align:left}th{background:#f2f2f2;font-size:8px}@page{size:A4 landscape;margin:7mm}</style></head><body><h2>${activeTab === "absent" ? "Long Absent Students" : "Low Attendance Students"}</h2><p>Session ${sessionLabel} • ${readableDate(filters.from_date)} to ${readableDate(filters.to_date)} • Total ${all.length}</p><table><thead><tr><th>#</th><th>Student</th><th>Reg No</th><th>Mobile</th><th>Class / Batch</th><th>Present</th><th>Absent</th><th>Marked</th><th>Attendance</th></tr></thead><tbody>${body}</tbody></table><script>window.onload=()=>setTimeout(()=>window.print(),200)</script></body></html>`,
    );
    w.document.close();
  };
  const reset = () => {
    const active = sessions.find((s) => Number(s?.status) === 1);
    setSearchInput("");
    setSearch("");
    setFilters({
      session_id: active?.id ? String(active.id) : "",
      from_date: daysAgo(29),
      to_date: today(),
      absent_days: "7",
      low_percentage: "75",
    });
  };
  const pages = useMemo(() => {
    const out = [];
    for (
      let p = Math.max(1, pagination.current_page - 2);
      p <= Math.min(pagination.last_page, pagination.current_page + 2);
      p++
    )
      out.push(p);
    return out;
  }, [pagination]);
  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar attendance-dashboard">
        <div className="layout-container">
          {/* SIDEBAR */}
          <div className="no-print">
            <Header />
          </div>
          <div className="layout-page risk-report-layout">
            {/* TOP NAVBAR */}
            <div className="no-print">
              <Nav />
            </div>
            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                {/* ATTENDANCE MASTER V2 */}
                <div className="no-print risk-master-v2-wrapper">
                  <MasterV2 />
                </div>
                <div className="risk-report-page">
                  {/* <div className="risk-page-header">
                    <div>
                      <h4>Attendance Attention Report</h4>
                      <p>
                        Long absent and low attendance students with complete
                        student-wise reporting
                      </p>
                    </div>
                    <div className="risk-actions">
                      <button
                        onClick={exportExcel}
                        disabled={!pagination.total}
                      >
                        <i className="ri-file-excel-2-line" /> Excel
                      </button>
                      <button
                        className="primary"
                        onClick={printReport}
                        disabled={!pagination.total}
                      >
                        <i className="ri-printer-line" /> Print
                      </button>
                    </div>
                  </div> */}
                  <div className="risk-tabs">
                    <button
                      className={activeTab === "absent" ? "active" : ""}
                      onClick={() => setActiveTab("absent")}
                    >
                      <i className="ri-calendar-close-line" />
                      <span>
                        <strong>Long Absent</strong>
                        <small>
                          Latest consecutive attendance records are absent
                        </small>
                      </span>
                    </button>
                    <button
                      className={activeTab === "low" ? "active" : ""}
                      onClick={() => setActiveTab("low")}
                    >
                      <i className="ri-arrow-down-circle-line" />
                      <span>
                        <strong>Low Attendance</strong>
                        <small>
                          Students below selected attendance percentage
                        </small>
                      </span>
                    </button>
                  </div>
                  <div className="risk-filter-card">
                    <div className="risk-filter-grid">
                      <label>
                        <span>Session</span>
                        <select
                          value={filters.session_id}
                          onChange={(e) =>
                            setFilters((p) => ({
                              ...p,
                              session_id: e.target.value,
                            }))
                          }
                        >
                          <option value="">Select Session</option>
                          {sessions.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.session || s.title}
                              {Number(s.status) === 1 ? " - Active" : ""}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        <span>From Date</span>
                        <input
                          type="date"
                          value={filters.from_date}
                          onChange={(e) =>
                            setFilters((p) => ({
                              ...p,
                              from_date: e.target.value,
                            }))
                          }
                        />
                      </label>
                      <label>
                        <span>To Date</span>
                        <input
                          type="date"
                          max={today()}
                          value={filters.to_date}
                          onChange={(e) =>
                            setFilters((p) => ({
                              ...p,
                              to_date: e.target.value,
                            }))
                          }
                        />
                      </label>
                      {activeTab === "absent" ? (
                        <label>
                          <span>Consecutive Absent</span>
                          <select
                            value={filters.absent_days}
                            onChange={(e) =>
                              setFilters((p) => ({
                                ...p,
                                absent_days: e.target.value,
                              }))
                            }
                          >
                            {[1, 2, 3, 5, 7, 10, 15, 30].map((v) => (
                              <option key={v} value={v}>
                                Latest {v} Day{v > 1 ? "s" : ""}
                              </option>
                            ))}
                          </select>
                        </label>
                      ) : (
                        <label>
                          <span>Attendance Below</span>
                          <select
                            value={filters.low_percentage}
                            onChange={(e) =>
                              setFilters((p) => ({
                                ...p,
                                low_percentage: e.target.value,
                              }))
                            }
                          >
                            {[75, 70, 60, 50, 40, 30].map((v) => (
                              <option key={v} value={v}>
                                Below {v}%
                              </option>
                            ))}
                          </select>
                        </label>
                      )}
                      <label className="risk-search">
                        <span>Search</span>
                        <div>
                          <i className="ri-search-line" />
                          <input
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Name, reg no., mobile..."
                          />
                        </div>
                      </label>
                    </div>
                    <div className="risk-filter-actions">
                      <button
                        onClick={() =>
                          setFilters((p) => ({
                            ...p,
                            from_date: daysAgo(29),
                            to_date: today(),
                          }))
                        }
                      >
                        Last 30 Days
                      </button>
                      <button onClick={reset}>
                        <i className="ri-refresh-line" /> Reset
                      </button>
                    </div>
                  </div>
                  <div className="risk-report-card">
                    <div className="risk-report-head">
                      <div>
                        <strong>
                          {activeTab === "absent"
                            ? "Long Absent Students"
                            : "Low Attendance Students"}
                        </strong>
                        <span>Session: {sessionLabel}</span>
                      </div>
                      <div>
                        <select
                          value={pagination.per_page}
                          onChange={(e) =>
                            setPagination((p) => ({
                              ...p,
                              per_page: Number(e.target.value),
                            }))
                          }
                        >
                          <option value="25">25 / page</option>
                          <option value="50">50 / page</option>
                          <option value="100">100 / page</option>
                          <option value="200">200 / page</option>
                        </select>
                        <button
                          onClick={() => loadReport(pagination.current_page)}
                        >
                          <i className="ri-refresh-line" /> Refresh
                        </button>
                        <div className="risk-actions">
                          <button
                            onClick={exportExcel}
                            disabled={!pagination.total}
                          >
                            <i className="ri-file-excel-2-line" /> Excel
                          </button>
                          <button
                            className="primary"
                            onClick={printReport}
                            disabled={!pagination.total}
                          >
                            <i className="ri-printer-line" /> Print
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="risk-mini-summary">
                      <div>
                        <span>Students</span>
                        <strong>{pagination.total}</strong>
                      </div>
                      <div>
                        <span>Present Days (Page)</span>
                        <strong className="green">{pageSummary.present}</strong>
                      </div>
                      <div>
                        <span>Absent Days (Page)</span>
                        <strong className="red">{pageSummary.absent}</strong>
                      </div>
                      <div>
                        <span>Marked Days (Page)</span>
                        <strong>{pageSummary.marked}</strong>
                      </div>
                    </div>
                    {rows.length ? (
                      <>
                        <div className="risk-table-wrap">
                          <table className="risk-table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Student</th>
                                <th>Reg. No.</th>
                                <th>Mobile</th>
                                <th>Class / Batch</th>
                                <th className="center">Present</th>
                                <th className="center">Absent</th>
                                <th className="center">Marked</th>
                                <th>Attendance</th>
                                <th>Absence / Risk</th>
                                <th className="center">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((s, i) => {
                                const st = s.__stats;
                                const [risk, riskLabel] = riskOf(st.percentage);
                                const no =
                                  (pagination.current_page - 1) *
                                    pagination.per_page +
                                  i +
                                  1;
                                return (
                                  <tr key={s.id || i}>
                                    <td className="center row-no">{no}</td>
                                    <td>
                                      <div className="risk-student">
                                        <span className="risk-avatar">
                                          {initials(s.name)}
                                        </span>
                                        <div>
                                          <strong>{s.name || "-"}</strong>
                                          <small>
                                            Father: {s.father_name || "-"}
                                          </small>
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <strong>
                                        {s.registration_no || "-"}
                                      </strong>
                                    </td>
                                    <td>{s.phone || "-"}</td>
                                    <td>
                                      <div className="class-cell">
                                        <strong>{batchName(s)}</strong>
                                        <small>{courseName(s)}</small>
                                      </div>
                                    </td>
                                    <td className="center">
                                      <span className="count-pill present">
                                        {st.present}
                                      </span>
                                    </td>
                                    <td className="center">
                                      <span className="count-pill absent">
                                        {st.absent}
                                      </span>
                                    </td>
                                    <td className="center">
                                      <strong>{st.marked}</strong>
                                    </td>
                                    <td>
                                      <div className="attendance-progress">
                                        <div>
                                          <strong>
                                            {st.percentage.toFixed(1)}%
                                          </strong>
                                          <span className={risk}>
                                            {riskLabel}
                                          </span>
                                        </div>
                                        <div className="bar">
                                          <span
                                            className={risk}
                                            style={{
                                              width: `${Math.min(100, st.percentage)}%`,
                                            }}
                                          />
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <span
                                        className={`risk-badge ${activeTab === "absent" ? "danger" : risk}`}
                                      >
                                        <i
                                          className={
                                            activeTab === "absent"
                                              ? "ri-calendar-close-line"
                                              : "ri-arrow-down-line"
                                          }
                                        />
                                        {activeTab === "absent"
                                          ? `Latest ${filters.absent_days} Absent`
                                          : `${st.percentage.toFixed(1)}%`}
                                      </span>
                                    </td>
                                    <td className="center">
                                      <button type="button" className="call-action-btn" title="Call student / View call history" onClick={() => openCallModal(s)}><i className="ri-phone-line" /> Call</button>
                                <button
                                        className="view-report-btn"
                                        onClick={() => openStudentReport(s)}
                                      >
                                        <i className="ri-eye-line" /> View
                                        Report
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <div className="risk-mobile-list">
                          {rows.map((s) => {
                            const st = s.__stats;
                            return (
                              <div
                                className="risk-mobile-card"
                                key={`m-${s.id}`}
                              >
                                <div className="mobile-student">
                                  <span className="risk-avatar">
                                    {initials(s.name)}
                                  </span>
                                  <div>
                                    <strong>{s.name}</strong>
                                    <small>
                                      {s.registration_no} • {batchName(s)}
                                    </small>
                                  </div>
                                  <b>{st.percentage.toFixed(0)}%</b>
                                </div>
                                <div className="mobile-stats">
                                  <span>
                                    Present<b className="green">{st.present}</b>
                                  </span>
                                  <span>
                                    Absent<b className="red">{st.absent}</b>
                                  </span>
                                  <span>
                                    Marked<b>{st.marked}</b>
                                  </span>
                                </div>
                              <button type="button" className="call-action-btn mobile" onClick={() => openCallModal(s)}><i className="ri-phone-line" /> Call / Follow-up</button>
                                <button
                                  className="view-report-btn mobile"
                                  onClick={() => openStudentReport(s)}
                                >
                                  <i className="ri-eye-line" /> View Full
                                  Attendance Report
                                </button>
                              </div>
                            );
                          })}
                        </div>
                        <div className="risk-pagination">
                          <span>
                            Showing{" "}
                            <b>
                              {(pagination.current_page - 1) *
                                pagination.per_page +
                                1}
                            </b>{" "}
                            -{" "}
                            <b>
                              {Math.min(
                                pagination.current_page * pagination.per_page,
                                pagination.total,
                              )}
                            </b>{" "}
                            of <b>{pagination.total}</b>
                          </span>
                          <div>
                            <button
                              disabled={pagination.current_page <= 1}
                              onClick={() =>
                                loadReport(pagination.current_page - 1)
                              }
                            >
                              <i className="ri-arrow-left-s-line" />
                            </button>
                            {pages.map((p) => (
                              <button
                                key={p}
                                className={
                                  p === pagination.current_page ? "active" : ""
                                }
                                onClick={() => loadReport(p)}
                              >
                                {p}
                              </button>
                            ))}
                            <button
                              disabled={
                                pagination.current_page >= pagination.last_page
                              }
                              onClick={() =>
                                loadReport(pagination.current_page + 1)
                              }
                            >
                              <i className="ri-arrow-right-s-line" />
                            </button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="risk-empty">
                        <i className="ri-user-search-line" />
                        <strong>No Students Found</strong>
                        <span>Try changing the selected filters.</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {callStudent && (
      <div className="call-modal-overlay" onMouseDown={closeCallModal}>
        <div className="call-modal-panel" role="dialog" aria-modal="true" aria-label="Student calling and follow-up" onMouseDown={(e) => e.stopPropagation()}>
          <div className="call-modal-heading">
            <div><small>ATTENDANCE CALLING</small><h4>{callStudent.name || "Student"}</h4><p>Father: {callStudent.father_name || "-"} · Reg: {callStudent.registration_no || "-"}</p></div>
            <button type="button" onClick={closeCallModal} aria-label="Close"><i className="ri-close-line" /></button>
          </div>
          <div className="call-modal-content">
            <div className="call-contact-card">
              <div><small>Mobile Number</small><strong>{callStudent.phone || "Not available"}</strong></div>
              {callStudent.phone && <a href={`tel:${String(callStudent.phone).replace(/[^+\d]/g, "")}`} className="call-dial-btn"><i className="ri-phone-fill" /> Dial Number</a>}
            </div>
            <h6 className="call-section-title">Previous Call Responses ({callHistory.length})</h6>
            {callLoading ? <div className="call-history-empty">Loading previous calls...</div> : callHistory.length ? (
              <div className="call-history-list">{callHistory.map((entry, index) => (
                <div className="call-history-entry" key={entry.id || index}>
                  <div className="call-history-icon"><i className="ri-phone-line" /></div>
                  <div><strong>{entry.response || entry.remarks || entry.remark || "Call recorded"}</strong>
                    <small>{entry.followup_date || entry.created_at || "-"} {entry.counselor?.name ? ` · ${entry.counselor.name}` : ""}</small>
                    {entry.next_followup_date && <span>Next follow-up: {readableDate(String(entry.next_followup_date).slice(0,10))}</span>}
                  </div>
                </div>
              ))}</div>
            ) : <div className="call-history-empty">No previous call responses found.</div>}
            <form onSubmit={saveCallResponse} className="call-response-form">
              <h6 className="call-section-title">Add Call Response</h6>
              <div className="call-quick-responses">{quickCallResponses.map((item) => <button type="button" key={item} className={callResponse === item ? "selected" : ""} onClick={() => setCallResponse(item)}>{item}</button>)}</div>
              <label>Response / Notes <span>*</span></label>
              <textarea required maxLength={1000} rows={3} value={callResponse} onChange={(e) => setCallResponse(e.target.value)} placeholder="Enter parent response or call notes..." />
              <label>Next Follow-up Date (optional)</label>
              <input type="date" min={today()} value={callNextDate} onChange={(e) => setCallNextDate(e.target.value)} />
              <div className="call-modal-actions"><button type="button" onClick={closeCallModal}>Close</button><button type="submit" disabled={callSaving || !callResponse.trim()}><i className="ri-save-line" /> {callSaving ? "Saving..." : "Save Response"}</button></div>
            </form>
          </div>
        </div>
      </div>
    )}
    <style>{`
  
    .call-action-btn{border:1px solid #16a34a;border-radius:7px;background:#ecfdf3;color:#15803d;padding:7px 10px;margin-right:6px;font-size:11px;font-weight:800;cursor:pointer;white-space:nowrap}.call-action-btn.mobile{display:flex;align-items:center;justify-content:center;width:100%;margin-bottom:8px;gap:5px}
    .call-modal-overlay{position:fixed;inset:0;z-index:11000;background:rgba(15,23,42,.65);display:flex;align-items:center;justify-content:center;padding:14px}.call-modal-panel{width:min(690px,100%);max-height:94vh;overflow:hidden;display:flex;flex-direction:column;background:var(--bs-body-bg,#fff);color:var(--bs-body-color,#333);border-radius:17px;box-shadow:0 20px 60px #0004}.call-modal-heading{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;padding:17px 20px;border-bottom:1px solid var(--bs-border-color,#ddd)}.call-modal-heading small{font-size:10px;color:#696cff;font-weight:800}.call-modal-heading h4{margin:3px 0;font-size:19px}.call-modal-heading p{margin:0;font-size:12px;color:#9299a4}.call-modal-heading>button{border:0;background:transparent;font-size:21px;color:inherit}.call-modal-content{overflow:auto;padding:18px 20px}.call-contact-card{display:flex;justify-content:space-between;align-items:center;gap:12px;background:rgba(105,108,255,.08);padding:13px;border-radius:12px}.call-contact-card small{display:block;font-size:11px;color:#9299a4}.call-contact-card strong{font-size:17px}.call-dial-btn{background:#16a34a;color:#fff!important;padding:10px 14px;border-radius:9px;text-decoration:none;font-size:12px;font-weight:800}.call-section-title{font-weight:800;margin:18px 0 11px}.call-history-list{max-height:230px;overflow:auto;display:flex;flex-direction:column;gap:8px}.call-history-entry{display:flex;gap:10px;padding:11px;border:1px solid var(--bs-border-color,#e5e7eb);border-radius:10px}.call-history-icon{width:31px;height:31px;flex-shrink:0;display:grid;place-items:center;border-radius:9px;background:#ecfdf3;color:#16a34a}.call-history-entry strong{display:block;font-size:12px}.call-history-entry small{display:block;color:#9299a4;margin-top:4px}.call-history-entry span{display:block;color:#696cff;font-size:11px;margin-top:4px}.call-history-empty{padding:18px;text-align:center;color:#9299a4;background:rgba(127,127,127,.04);border-radius:9px}.call-response-form{margin-top:15px;border-top:1px solid var(--bs-border-color,#ddd)}.call-response-form label{display:block;font-size:12px;font-weight:750;margin:12px 0 6px}.call-response-form label span{color:red}.call-response-form textarea,.call-response-form input{display:block;width:100%;border:1px solid var(--bs-border-color,#ddd);background:var(--bs-body-bg,#fff);color:var(--bs-body-color,#333);border-radius:8px;padding:10px;font-size:12px}.call-quick-responses{display:flex;flex-wrap:wrap;gap:6px}.call-quick-responses button{border:1px solid var(--bs-border-color,#ddd);background:transparent;color:inherit;padding:6px 9px;border-radius:8px;font-size:10px}.call-quick-responses button.selected{border-color:#696cff;color:#696cff;background:rgba(105,108,255,.09)}.call-modal-actions{display:flex;justify-content:flex-end;gap:8px;margin-top:18px}.call-modal-actions button{border:1px solid var(--bs-border-color,#ddd);border-radius:8px;padding:9px 14px;background:transparent;color:inherit;font-weight:750}.call-modal-actions button[type=submit]{background:#696cff;color:white;border-color:#696cff}.call-modal-actions button:disabled{opacity:.5}@media(max-width:575px){.call-modal-overlay{padding:5px}.call-modal-panel{max-height:98vh}.call-modal-content{padding:12px}.call-contact-card{flex-wrap:wrap}}@media print{.call-modal-overlay,.call-action-btn{display:none!important}}
    .risk-report-layout{min-width:0;width:100%}.risk-report-page{max-width:1750px;margin:0 auto;color:#454b55}.risk-page-header,.risk-tabs,.risk-filter-card,.risk-report-card{background:#fff;border:1px solid #e3e7ed;border-radius:12px;box-shadow:0 4px 18px rgba(31,45,61,.035)}.risk-page-header{padding:16px 18px;display:flex;justify-content:space-between;align-items:center;margin-bottom:11px}.risk-page-header h4{margin:0;font-size:20px;font-weight:850}.risk-page-header p{margin:4px 0 0;font-size:12px;color:#9097a2}.risk-actions{display:flex;gap:7px}.risk-actions button{min-height:38px;border:1px solid #dfe3e8;background:#fff;border-radius:8px;padding:0 12px;font-size:11px;font-weight:800;color:#59616c}.risk-actions button.primary{background:#696cff;border-color:#696cff;color:#fff}.risk-actions button:disabled{opacity:.45}.risk-tabs{display:grid;grid-template-columns:1fr 1fr;overflow:hidden;margin-bottom:11px}.risk-tabs button{border:0;background:#fff;padding:13px 15px;display:flex;align-items:center;gap:10px;text-align:left;color:#626a75}.risk-tabs button:first-child{border-right:1px solid #e6e9ed}.risk-tabs button.active{background:#f7f7ff;box-shadow:inset 0 -3px #696cff}.risk-tabs button>i{font-size:22px;color:#696cff}.risk-tabs button span{display:flex;flex-direction:column}.risk-tabs strong{font-size:13px}.risk-tabs small{font-size:10px;color:#949ba6;margin-top:2px}
      .risk-filter-card{padding:13px 14px;margin-bottom:11px}.risk-filter-grid{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:9px}.risk-filter-grid label>span{display:block;font-size:10px;font-weight:750;color:#737b86;margin:0 0 5px 2px}.risk-filter-grid select,.risk-filter-grid input{width:100%;height:39px;border:1px solid #dce1e7;border-radius:8px;padding:0 10px;background:#fff;color:#4e5560;font-size:11px;outline:none}.risk-search>div{position:relative}.risk-search i{position:absolute;left:10px;top:12px;color:#999}.risk-search input{padding-left:30px}.risk-filter-actions{display:flex;justify-content:flex-end;gap:6px;margin-top:9px}.risk-filter-actions button{border:1px solid #dfe3e8;background:#fff;border-radius:7px;padding:7px 10px;font-size:10px;font-weight:750;color:#626a75}
      .risk-report-card{overflow:hidden}.risk-report-head{padding:12px 14px;border-bottom:1px solid #e8ebef;display:flex;justify-content:space-between;align-items:center}.risk-report-head>div:first-child{display:flex;align-items:baseline;gap:7px}.risk-report-head strong{font-size:14px}.risk-report-head span{font-size:10px;color:#969da8}.risk-report-head>div:last-child{display:flex;gap:6px}.risk-report-head select,.risk-report-head button{height:34px;border:1px solid #dfe3e8;background:#fff;border-radius:7px;padding:0 9px;color:#606873;font-size:10px;font-weight:750}.risk-mini-summary{display:grid;grid-template-columns:repeat(4,1fr);background:#fafbfc;border-bottom:1px solid #e8ebef}.risk-mini-summary>div{padding:10px 13px;border-right:1px solid #e8ebef;display:flex;flex-direction:column}.risk-mini-summary>div:last-child{border-right:0}.risk-mini-summary span{font-size:9px;color:#9299a4}.risk-mini-summary strong{font-size:14px;margin-top:2px}.green{color:#278b53!important}.red{color:#d34849!important}
      .risk-table-wrap{overflow-x:auto}.risk-table{width:100%;min-width:1350px;border-collapse:collapse}.risk-table th{padding:12px 10px;background:#f6f7f9;border-bottom:1px solid #dfe3e8;color:#616a76;font-size:11px;font-weight:850;text-align:left;white-space:nowrap}.risk-table td{padding:12px 10px;border-bottom:1px solid #eceff2;color:#4d545f;font-size:12px;vertical-align:middle}.risk-table tbody tr:hover{background:#fafbff}.risk-table .center{text-align:center}.row-no{color:#7e8691!important;font-weight:750}.risk-student{display:flex;align-items:center;gap:9px;min-width:205px}.risk-avatar{width:38px;height:38px;flex:0 0 38px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:#eef0ff;color:#696cff;font-size:11px;font-weight:900}.risk-student>div,.class-cell{display:flex;flex-direction:column}.risk-student strong,.class-cell strong{font-size:12px;color:#444b55}.risk-student small,.class-cell small{font-size:9px;color:#969da8;margin-top:2px}.count-pill{display:inline-flex;min-width:36px;justify-content:center;padding:6px 8px;border-radius:7px;font-size:11px;font-weight:900}.count-pill.present{background:#e8f8ef;color:#278b53}.count-pill.absent{background:#ffeded;color:#d34849}.attendance-progress{min-width:190px}.attendance-progress>div:first-child{display:flex;justify-content:space-between;align-items:center;margin-bottom:6px}.attendance-progress strong{font-size:11px}.attendance-progress span{font-size:9px;font-weight:850}.bar{height:7px;background:#e9ecf0;border-radius:10px;overflow:hidden}.bar span{display:block;height:100%;border-radius:10px}.good{color:#278b53!important}.warning{color:#b9781c!important}.danger,.critical{color:#d34849!important}.bar span.good{background:#3ca66a}.bar span.warning{background:#dca13c}.bar span.danger,.bar span.critical{background:#d34849}.risk-badge{display:inline-flex;align-items:center;gap:5px;padding:6px 8px;border-radius:7px;background:#ffeded;font-size:10px;font-weight:850;white-space:nowrap}.view-report-btn{border:1px solid #696cff;background:#f7f7ff;color:#5f62e8;border-radius:7px;padding:7px 10px;font-size:10px;font-weight:850;display:inline-flex;align-items:center;gap:5px;white-space:nowrap}.view-report-btn:hover{background:#696cff;color:#fff}.risk-pagination{padding:11px 13px;border-top:1px solid #e8ebef;display:flex;justify-content:space-between;align-items:center;font-size:10px;color:#838b96}.risk-pagination>div{display:flex;gap:4px}.risk-pagination button{min-width:31px;height:31px;border:1px solid #dfe3e8;background:#fff;border-radius:6px;font-size:10px}.risk-pagination button.active{background:#696cff;border-color:#696cff;color:#fff}.risk-pagination button:disabled{opacity:.4}.risk-empty{padding:45px;display:flex;align-items:center;flex-direction:column;color:#9299a4}.risk-empty>i{font-size:34px;margin-bottom:8px}.risk-empty strong{font-size:14px;color:#5d6570}.risk-empty span{font-size:10px;margin-top:3px}.risk-mobile-list{display:none}
      @media(max-width:1100px){.risk-filter-grid{grid-template-columns:repeat(3,1fr)}}@media(max-width:767px){.risk-report-page{padding-left:9px!important;padding-right:9px!important}.risk-page-header{align-items:flex-start;flex-direction:column;gap:10px}.risk-actions{width:100%}.risk-actions button{flex:1}.risk-tabs{grid-template-columns:1fr}.risk-tabs button:first-child{border-right:0;border-bottom:1px solid #e6e9ed}.risk-filter-grid{grid-template-columns:repeat(2,1fr)}.risk-table-wrap{display:none}.risk-mobile-list{display:flex;flex-direction:column}.risk-mobile-card{padding:12px;border-bottom:1px solid #e8ebef}.mobile-student{display:flex;align-items:center;gap:8px}.mobile-student>div{display:flex;flex:1;min-width:0;flex-direction:column}.mobile-student strong{font-size:12px}.mobile-student small{font-size:9px;color:#969da8}.mobile-student>b{font-size:12px}.mobile-stats{display:grid;grid-template-columns:repeat(3,1fr);gap:6px;margin:9px 0}.mobile-stats span{background:#f6f7f9;border-radius:7px;padding:7px;display:flex;flex-direction:column;font-size:8px;color:#9299a4}.mobile-stats b{font-size:11px;margin-top:2px}.view-report-btn.mobile{width:100%;justify-content:center}.risk-mini-summary{grid-template-columns:repeat(2,1fr)}.risk-pagination{align-items:flex-start;flex-direction:column;gap:8px}}@media(max-width:480px){.risk-filter-grid{grid-template-columns:1fr}.risk-page-header h4{font-size:17px}}
      .attendance-dashboard{min-height:100vh;background:#f6f7fb}
      .attendance-dashboard .layout-container{min-height:100vh}
      .risk-master-v2-wrapper{width:100%;min-width:0;margin-bottom:14px}
      @media(max-width:991.98px){.risk-master-v2-wrapper{margin-bottom:12px}}
      @media(max-width:575.98px){.risk-master-v2-wrapper{margin-bottom:10px}}
      @media print{.attendance-dashboard .no-print,.risk-master-v2-wrapper{display:none!important}}
`}</style>
    
      {/* Attendance-only layout fix: remove fixed-template navbar offset. */}
      <style>{`
        html.layout-navbar-fixed .attendance-dashboard.layout-wrapper,
        html.layout-navbar-fixed .attendance-dashboard.layout-wrapper > .layout-container,
        .attendance-dashboard.layout-wrapper > .layout-container {
          padding-top: 0 !important;
          margin-top: 0 !important;
        }
        html.layout-navbar-fixed .attendance-dashboard.layout-wrapper .layout-page,
        html.layout-navbar-fixed .layout-wrapper:not(.layout-without-menu).attendance-dashboard .layout-page,
        .attendance-dashboard.layout-wrapper .layout-page {
          padding-top: 0 !important;
          margin-top: 0 !important;
          min-width: 0;
        }
        .attendance-dashboard.layout-wrapper #layout-navbar.scai-topbar,
        html.layout-navbar-fixed .attendance-dashboard.layout-wrapper #layout-navbar {
          top: 0 !important;
          left: auto !important;
          right: auto !important;
          width: 100% !important;
          max-width: none !important;
          margin: 0 !important;
          padding: 0 !important;
          border-radius: 0 !important;
        }
        @media print {
          .attendance-dashboard.layout-wrapper #layout-navbar { display: none !important; }
        }
      `}</style>
</>
  );
}
